// Real AI chat client — streams POST /api/chat (ai-service) over SSE.
//
// The backend wraps Vercel AI SDK output in the same hand-rolled
// `event:`/`data:` framing the generation streams use:
//   event: session  data: {"sessionId":"..."}
//   event: text     data: {"delta":"..."}
//   event: action   data: {"name":"add_game","params":{...}}
//   event: done     data: {}
//   event: error    data: {"message":"..."}
//
// Tool calls arrive as `action` events — the FE owns the resulting UX
// (e.g. kicking off the add-game generation stream).

import { getAccessToken } from '@/lib/apiClient'

export interface ChatContextPayload {
  module?: string
  view?: string
  guideId?: string
  guideTitle?: string
  learnerId?: string
  learnerName?: string
  learnerStage?: string
  generationInProgress?: boolean
}

// SSE `action` event payload. Newer backends send id/label/autoDispatch;
// the fallbacks in the stream handler keep old-backend responses working
// (add_game was the only action and always dispatched immediately).
export interface ChatAction {
  id?: string
  name: string
  label?: string
  // True → dispatch immediately (add_game / theater mode); false → render
  // as an inline button on the assistant message and dispatch on click.
  autoDispatch?: boolean
  params: {
    guideId?: string
    learnerId?: string
    bookId?: string
    bookTitle?: string
    category?: 'pattern_practice' | 'vocabulary' | 'fluency' | 'comprehension'
    focus?: string
  } & Record<string, unknown>
}

export interface ChatWireMessage {
  role: 'user' | 'assistant'
  content: string
}

interface StreamChatHandlers {
  onSessionId?: (sessionId: string) => void
  onChunk: (delta: string) => void
  onAction?: (action: ChatAction) => void
  onDone: () => void
  onError: (message: string) => void
}

export function streamChat(
  messages: ChatWireMessage[],
  context: ChatContextPayload,
  sessionId: string | null,
  handlers: StreamChatHandlers,
): AbortController {
  const controller = new AbortController()

  const run = async () => {
    const token = await getAccessToken()
    const baseUrl = import.meta.env.VITE_API_URL_AI
    const response = await fetch(`${baseUrl}/api/chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        messages,
        context,
        ...(sessionId ? { sessionId } : {}),
      }),
      signal: controller.signal,
    })

    if (!response.ok) {
      const body = await response.json().catch(() => null)
      throw new Error(body?.error?.message ?? body?.error ?? `Chat failed (${response.status})`)
    }

    const reader = response.body?.getReader()
    if (!reader) throw new Error('No response stream')

    const decoder = new TextDecoder()
    let buffer = ''
    let currentEvent = ''

    while (true) {
      const { done, value } = await reader.read()
      if (done) break

      buffer += decoder.decode(value, { stream: true })
      const lines = buffer.split('\n')
      buffer = lines.pop() ?? ''

      for (const line of lines) {
        if (line.startsWith('event:')) {
          currentEvent = line.slice(6).trim()
        } else if (line.startsWith('data:')) {
          const data = line.slice(5).trim()
          try {
            const parsed = JSON.parse(data)
            if (currentEvent === 'session' && parsed.sessionId) {
              handlers.onSessionId?.(parsed.sessionId)
            } else if (currentEvent === 'text' && typeof parsed.delta === 'string') {
              handlers.onChunk(parsed.delta)
            } else if (currentEvent === 'action' && parsed.name) {
              handlers.onAction?.(parsed as ChatAction)
            } else if (currentEvent === 'error') {
              handlers.onError(parsed.message ?? 'The assistant hit an error.')
            }
            // `done` falls through — the read loop ends when the server closes.
          } catch {
            /* skip malformed frames */
          }
        }
      }
    }

    handlers.onDone()
  }

  run().catch((err) => {
    if (controller.signal.aborted) return
    handlers.onError(err instanceof Error ? err.message : 'Chat request failed.')
  })

  return controller
}
